import React, { useContext, useEffect } from 'react';
import styles from './lista.module.scss';
import { Col, Row } from 'react-dsgov';
import { BrButton, BrCard, BrDivider, BrLoading } from '@govbr-ds/react-components';
import {
  BasesPrimeiraVenda,
  IDadosOutrosRegimes,
  IListaBaseCalculo,
  PaginasOutrosRegimes,
} from '../../../../../../../../../interfaces/IDadosOutrosRegimes';
import { formatarNrProcesso } from '../../../../../../../../../utils/formatNrProcesso';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from '../../../../../../../../../utils/formatCurrency';
import ErrorMessage from '../../../../../../../../../components/errorMessage/errorMessage';
import { formatarCNPJOrCpf } from '../../../../../../../../../utils/formatCNPJOrCPF';
import ListaReceitaBruta from './components/listaReceitaBruta/listaReceitaBruta';
import ListaVendaNacional from './components/listaVendaNacional/listaVendaNacional';
import ListaReceitaBrutaCalculada from './components/listaReceitaBrutaCalculada/listaReceitaBrutaCalculada';
import ListaBalneario from './components/listaBalneario/listaBalneario';
import ListaReceitaExportacoes from './components/listaReceitaExportacoes/listaReceitaExportacoes';
import SelectBaseCalculo from './components/selectBaseCalculo/selectBaseCalculo';
import { Tooltip } from 'react-tooltip';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IListaOutrosRegimesProps {
  setStepFormulario: React.Dispatch<React.SetStateAction<number>>;
  setBaseDeCalculo: React.Dispatch<React.SetStateAction<PaginasOutrosRegimes>>;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  dadoOperacao: IDadosOutrosRegimes | undefined;
  refetchDados: () => void;
  isRefetching: boolean;
  isLoadingDadosOperacao: boolean;
}

const ListaOutrosRegimes = ({
  setStepFormulario,
  setBaseDeCalculo,
  setIsEdicao,
  dadoOperacao,
  refetchDados,
  isRefetching,
  isLoadingDadosOperacao,
}: IListaOutrosRegimesProps) => {
  const context = useContext(DiefCfemContext);
  const idOperacao = context?.idOperacaoEditar || '';

  useEffect(() => {
    context?.setIdBemTerceiro(-1);
    context?.setIdBalneario(-1);
    context?.setIdReceitaBruta(-1);
    context?.setIdVendaNacional(-1);
    context?.setIdReceitaBrutaCalculada(-1);
    context?.setIdReceitaExportacoes(-1);
    setIsEdicao(false);
    refetchDados();
    refetchLista();
  }, []);

  const { getListaBaseCalculoOutrosRegimes } = useBackEndRequests();

  const {
    data: listaBaseCalculo,
    isLoading: isLoadingListaBaseCalculo,
    error: listaBaseCalculoError,
    refetch: refetchLista,
    isRefetching: isRefetchingListaBaseCalculo,
  } = useQuery<IListaBaseCalculo, IErrorWithSentryId>({
    queryKey: ['listaBaseCalculoOutrosRegimes', idOperacao],
    queryFn: () => getListaBaseCalculoOutrosRegimes(idOperacao),
    refetchOnWindowFocus: false,
    enabled: idOperacao !== '',
    staleTime: 0,
    refetchOnMount: true,
  });

  const isListaVazia = (listaBaseCalculo: IListaBaseCalculo, isConsumo: boolean) => {
    if (isConsumo && listaBaseCalculo?.basesCalculoFatoGeradorConsumo) {
      const dados = listaBaseCalculo.basesCalculoFatoGeradorConsumo;
      if (dados.receitasBrutasCalculadas && dados.receitasBrutasCalculadas.length > 0) return false;
      else return true;
    } else if (!isConsumo && listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda) {
      const dados = listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda;
      if (
        (dados.balnearioOutrosValores && dados.balnearioOutrosValores.length > 0) ||
        (dados.balnearioValorBanho && dados.balnearioValorBanho.length > 0) ||
        (dados.receitaBrutaVendas && dados.receitaBrutaVendas.length > 0) ||
        (dados.vendasNacionais && dados.vendasNacionais.length > 0) ||
        (dados.receitaCalculadaExportacoes && dados.receitaCalculadaExportacoes.length > 0)
      )
        return false;
      else return true;
    } else return true;
  };

  const isTipoBaseCalculoComDados = (type: BasesPrimeiraVenda) => {
    const dados = listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda;
    if (!dados || !(type in dados)) return false;
    const valor = dados[type as keyof typeof dados];
    return Array.isArray(valor) && valor.length > 0;
  };

  const primeiraSaidaPorVendaRender = () => {
    if (listaBaseCalculo && isListaVazia(listaBaseCalculo, false)) {
      return <>Ainda não há formações de base de cálculo adicionadas para Primeira Saída por Venda</>;
    } else {
      return (
        <>
          {(isTipoBaseCalculoComDados('balnearioValorBanho') ||
            isTipoBaseCalculoComDados('balnearioOutrosValores')) && (
            <ListaBalneario
              itensComBanho={listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda.balnearioValorBanho}
              itensSemBanho={listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda.balnearioOutrosValores}
              setBaseDeCalculo={setBaseDeCalculo}
              setIsEdicao={setIsEdicao}
              refetchLista={refetchLista}
              refetchDados={refetchDados}
            />
          )}
          {isTipoBaseCalculoComDados('receitaBrutaVendas') && (
            <ListaReceitaBruta
              receitaBruta={listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda.receitaBrutaVendas[0]}
              setBaseDeCalculo={setBaseDeCalculo}
              setIsEdicao={setIsEdicao}
              refetchLista={refetchLista}
              refetchDados={refetchDados}
            />
          )}

          {isTipoBaseCalculoComDados('vendasNacionais') && (
            <ListaVendaNacional
              vendasNacionais={listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda.vendasNacionais || []}
              setBaseDeCalculo={setBaseDeCalculo}
              setIsEdicao={setIsEdicao}
              refetchLista={refetchLista}
              refetchDados={refetchDados}
            />
          )}
          {isTipoBaseCalculoComDados('receitaCalculadaExportacoes') && (
            <ListaReceitaExportacoes
              receitaExportacoes={
                listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda.receitaCalculadaExportacoes[0]
              }
              setBaseDeCalculo={setBaseDeCalculo}
              setIsEdicao={setIsEdicao}
              refetchLista={refetchLista}
              refetchDados={refetchDados}
            />
          )}
        </>
      );
    }
  };

  const consumoRender = () => {
    if (listaBaseCalculo && isListaVazia(listaBaseCalculo, true)) {
      return <>Ainda não há formações de base de cálculo adicionadas para Consumo</>;
    } else {
      return (
        <>
          {listaBaseCalculo?.basesCalculoFatoGeradorConsumo?.receitasBrutasCalculadas &&
            listaBaseCalculo?.basesCalculoFatoGeradorConsumo?.receitasBrutasCalculadas.length > 0 && (
              <ListaReceitaBrutaCalculada
                receitaBrutaCalculada={listaBaseCalculo?.basesCalculoFatoGeradorConsumo?.receitasBrutasCalculadas[0]}
                refetchDados={refetchDados}
                refetchLista={refetchLista}
                setBaseDeCalculo={setBaseDeCalculo}
                setIsEdicao={setIsEdicao}
              />
            )}
        </>
      );
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.dadosContainer}>
        {isRefetching || isLoadingDadosOperacao ? (
          <div className={styles.loadingContainer}>
            <BrLoading large />
          </div>
        ) : (
          <>
            <Row>
              <Col>
                <span className={styles.label}>Processo Minerário</span>
                <p className={styles.text}>{formatarNrProcesso(dadoOperacao?.numeroProcessoMinerario || '')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Substância</span>
                <p className={styles.text}>{dadoOperacao?.substancia}</p>
              </Col>
              <Col>
                <span className={styles.label}>Município/UF</span>
                <p className={styles.text}>
                  {dadoOperacao?.municipio}/{dadoOperacao?.uf}
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                <span className={styles.label}>Valor da Operação (R$)</span>
                <p className={styles.text}>{formatCurrency(dadoOperacao?.valorOperacao ?? '-')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Deduções</span>
                <p className={styles.text}>{formatCurrency(dadoOperacao?.deducoes ?? '-')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Período de Apuração</span>
                <p className={styles.text}>{dadoOperacao?.periodoApuracao}</p>
              </Col>
            </Row>
            <Row>
              <Col>
                <span className={styles.label}>Estabelecimento Minerador</span>
                <p className={styles.text}>{formatarCNPJOrCpf(dadoOperacao?.estabelecimentoMinerador ?? '')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Tipo de Vínculo</span>
                <p className={styles.text}>{dadoOperacao?.tipoVinculo === 1 ? 'Titular' : 'Arrendatário'}</p>
              </Col>
              <Col>
                <span className={styles.label}>Houve Produção de Bem Mineral?</span>
                <p className={styles.text}>{dadoOperacao?.houveProducaoBemMineral ? 'Sim' : 'Não'}</p>
              </Col>
            </Row>
            <Row>
              <Col size={4}>
                <span className={styles.label}>Houve Fato Gerador da CFEM?</span>
                <p className={styles.text}>{dadoOperacao?.houveFatoGerador ? 'Sim' : 'Não'}</p>
              </Col>
              <Col size={8}>
                <span className={styles.label}>Observações</span>
                <p className={styles.textArea}>{dadoOperacao?.observacoes || '-'}</p>
              </Col>
            </Row>
            <div className={styles.editButton}>
              <BrButton
                circle
                icon='fas fa-edit fa-md'
                onClick={() => setStepFormulario(1)}
                data-tooltip-id='edit-tooltip'
              />
              <Tooltip id='edit-tooltip' place='top' className={styles.tooltip} variant='info'>
                <div className={styles.tooltipText}>
                  Clique aqui para voltar para o passo 1 e editar os dados da operação
                </div>
              </Tooltip>
            </div>
          </>
        )}
      </div>
      <div className={styles.divider}>
        <BrDivider />
      </div>
      <div className={styles.basesDeCalculoContainer}>
        <div className={styles.subTitle}>Formações da Base de Cálculo Adicionadas</div>
        {isRefetchingListaBaseCalculo ? (
          <BrLoading large />
        ) : (
          <SelectBaseCalculo
            idOperacao={idOperacao}
            baseCalculoCadastradas={listaBaseCalculo}
            entrarModoEdicao={(baseCalculoSelecionada) => {
              setIsEdicao(false);
              setBaseDeCalculo(baseCalculoSelecionada);
            }}
          />
        )}
        {listaBaseCalculoError ? (
          <ErrorMessage errorMessage={listaBaseCalculoError.message} error={listaBaseCalculoError} />
        ) : (
          <div className={styles.baseCalculoListContainer}>
            {isLoadingListaBaseCalculo || isRefetchingListaBaseCalculo ? (
              <div className={styles.loadingContainer}>
                <BrLoading large />
              </div>
            ) : (
              <div className={styles.lista}>
                <BrCard className={styles.cardSecao}>
                  <div className={styles.cardSecaoContent}>
                    <span className={styles.cardSecaoTitle}>Primeira Saída por Venda</span>

                    <div className={styles.cardSecaoList}>{primeiraSaidaPorVendaRender()}</div>
                    <div className={styles.cardValues}>
                      <span>
                        Receita Total:{' '}
                        {formatCurrency(
                          listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda?.receitaTotal || '-'
                        )}
                      </span>
                      <span>
                        Deduções Totais:{' '}
                        {formatCurrency(
                          listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda?.deducoes || '-'
                        )}
                      </span>
                      <span>
                        Base de Cálculo Total:{' '}
                        {formatCurrency(
                          listaBaseCalculo?.basesCalculoFatoGeradorPrimeiraSaidaPorVenda?.baseCalculo || '-'
                        )}
                      </span>
                    </div>
                  </div>
                </BrCard>
                <BrCard className={styles.cardSecao}>
                  <div className={styles.cardSecaoContent}>
                    <span className={styles.cardSecaoTitle}>Consumo</span>
                    <div className={styles.cardSecaoList}>{consumoRender()}</div>
                    <div className={styles.cardValues}>
                      <span>
                        Receita Total:{' '}
                        {formatCurrency(listaBaseCalculo?.basesCalculoFatoGeradorConsumo?.receitaTotal || '-')}
                      </span>
                      <span>
                        Deduções Totais:{' '}
                        {formatCurrency(listaBaseCalculo?.basesCalculoFatoGeradorConsumo?.deducoes || '-')}
                      </span>
                      <span>
                        Base de Cálculo Total:{' '}
                        {formatCurrency(listaBaseCalculo?.basesCalculoFatoGeradorConsumo?.baseCalculo || '-')}
                      </span>
                    </div>
                  </div>
                </BrCard>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaOutrosRegimes;
